import { type FC, type ReactNode } from 'react';
import Close from 'dibs-icons/exports/legacy/Close.ts';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { isFacetSearch } from './helpers/isFacetSearch.ts';
import { FACET_TYPES } from './constants.ts';

import styles from './PhotosSearchBrowseFacetAppliedFilterTags.scss';

import { type FacetType, type FacetSearchPayload, type ModifyFacetHandler } from './types.ts';

const messages = defineMessages({
    dismissFacet: {
        id: 'abt.PhotosSearchBrowseFacetAppliedFilterTags.dismissFacet',
        defaultMessage: 'Remove "{facetLabel}"',
    },
});

type PhotosSearchBrowseFacetAppliedFilterTagsProps = {
    originalUrl?: string;
    facetSearch: FacetSearchPayload;
    onDelete: ModifyFacetHandler;
};

type PhotosSearchBrowseFacetTagProps = {
    facetLabel: ReactNode;
    onDelete: ModifyFacetHandler;
    facetType: FacetType;
    label: string;
    value: string;
};

const PhotosSearchBrowseFacetTag: FC<PhotosSearchBrowseFacetTagProps> = ({
    facetType,
    facetLabel,
    label,
    value,
    onDelete,
}) => {
    const intl = useIntl();
    return (
        <div data-tn={`applied-filters-item-${value}`} className={styles.tagItem}>
            <div>
                {facetLabel}:{' '}
                <span className={styles.tagLink} data-tn="applied-filters-copy">
                    {label}
                </span>
            </div>
            <Link
                buyerLinkType="standardLinkNoEffect"
                onClick={() => onDelete({ facetType, value, label })}
                ariaLabel={intl.formatMessage(messages.dismissFacet, { facetLabel: label })}
            >
                <Close className={styles.tagRemoveIcon} />
            </Link>
        </div>
    );
};

export const PhotosSearchBrowseFacetAppliedFilterTags: FC<
    PhotosSearchBrowseFacetAppliedFilterTagsProps
> = ({ originalUrl, facetSearch, onDelete }) => {
    const searchTerm = facetSearch?.searchTerm;
    const facetStyle = facetSearch?.style || [];
    const facetRoom = facetSearch?.room || [];
    const facetProject = facetSearch?.project || [];
    const facetCreator = facetSearch?.creator || [];

    const hideTags = !isFacetSearch(facetSearch);

    if (hideTags) {
        return null;
    }

    const facetTagProps = {
        facetSearch,
        originalUrl,
        onDelete,
    };

    return (
        <div className={styles.wrapper}>
            {!!searchTerm && (
                <PhotosSearchBrowseFacetTag
                    {...facetTagProps}
                    facetType={FACET_TYPES.SEARCH}
                    facetLabel={
                        <FormattedMessage
                            id="abt.PhotosSearchBrowseFacetAppliedFilterTags.searchWithin"
                            defaultMessage="Search Within"
                        />
                    }
                    value={searchTerm}
                    label={searchTerm}
                />
            )}
            {facetStyle.map(style => (
                <PhotosSearchBrowseFacetTag
                    {...facetTagProps}
                    key={`style-${style.value}`}
                    facetType={FACET_TYPES.STYLE}
                    facetLabel={
                        <FormattedMessage
                            id="abt.PhotosSearchBrowseFacetAppliedFilterTags.style"
                            defaultMessage="Style"
                        />
                    }
                    value={style.value}
                    label={style.label}
                />
            ))}
            {facetRoom.map(room => (
                <PhotosSearchBrowseFacetTag
                    {...facetTagProps}
                    key={`room-${room.value}`}
                    facetType={FACET_TYPES.ROOM}
                    facetLabel={
                        <FormattedMessage
                            id="abt.PhotosSearchBrowseFacetAppliedFilterTags.room"
                            defaultMessage="Room"
                        />
                    }
                    value={room.value}
                    label={room.label}
                />
            ))}
            {facetProject.map(project => (
                <PhotosSearchBrowseFacetTag
                    {...facetTagProps}
                    key={`project-${project.value}`}
                    facetType={FACET_TYPES.PROJECT}
                    facetLabel={
                        <FormattedMessage
                            id="abt.PhotosSearchBrowseFacetAppliedFilterTags.project"
                            defaultMessage="Project"
                        />
                    }
                    value={project.value}
                    label={project.label}
                />
            ))}
            {facetCreator.map(creator => (
                <PhotosSearchBrowseFacetTag
                    {...facetTagProps}
                    key={`creator-${creator.value}`}
                    facetType={FACET_TYPES.CREATOR}
                    facetLabel={
                        <FormattedMessage
                            id="abt.PhotosSearchBrowseFacetAppliedFilterTags.creator"
                            defaultMessage="Creator"
                        />
                    }
                    value={creator.value}
                    label={creator.label}
                />
            ))}
        </div>
    );
};
